import type {
  JournalEditionDTO,
  JournalEditionShortDTO,
  JournalListItemDTO,
} from '~/shared/api/client/journals/journals.dto';
import {
  JOURNAL_ISSUE_URL_PREFIX,
  JOURNAL_YEAR_URL_PREFIX,
} from '~/shared/api/client/journals/journals.const';

export const JOURNAL_BASE_PATH = '/journal';

/** The media kit is a page of its own, not a tab of the reference */
export const JOURNAL_ADVERTISING_SLUG = 'advertising';
export const JOURNAL_ADVERTISING_URL = `${JOURNAL_BASE_PATH}/${JOURNAL_ADVERTISING_SLUG}`;

export const JOURNAL_ROUTE_NAMES = {
  index: 'journal',
  section: 'journal-section',
} as const;

export function normalizeJournalPath(path: string): string {
  return path.replace(/\/+$/, '') || JOURNAL_BASE_PATH;
}

/**
 * Tabs of the tab bar, in order. A tab with no edition left in it is dropped:
 * its page would be a heading over nothing
 */
export function getJournalTabs(
  reference: JournalEditionDTO[],
): JournalEditionDTO[] {
  return reference
    .filter(
      (row) =>
        row.parent === null &&
        reference.some((child) => child.parent === row.id),
    )
    .sort((a, b) => a.order - b.order);
}

/** Chips of a tab, in the order the editors dragged them into */
export function getJournalTabEditions(
  reference: JournalEditionDTO[],
  tab: JournalEditionShortDTO,
): JournalEditionDTO[] {
  return reference
    .filter((row) => row.parent === tab.id)
    .sort((a, b) => a.order - b.order);
}

function isFirstJournalTab(
  reference: JournalEditionDTO[],
  tab: JournalEditionShortDTO,
): boolean {
  return getJournalTabs(reference)[0]?.id === tab.id;
}

/**
 * '/journal/guides' — or '/journal' for the first tab: the section opens on it,
 * and its own address only redirects there
 */
export function getJournalTabUrl(
  reference: JournalEditionDTO[],
  tab: JournalEditionShortDTO,
): string {
  return isFirstJournalTab(reference, tab)
    ? JOURNAL_BASE_PATH
    : `${JOURNAL_BASE_PATH}/${tab.slug}`;
}

/**
 * '/journal/guides/cre-retail', or '/journal/cre100' in the first tab. The tab
 * prefix only reads well: cre.ru resolves an edition by its last segment and
 * ignores whatever stands before it, so '/journal/special/cre100' keeps working
 * while no tab is called special (ADR 0001, decision 26)
 */
export function getJournalEditionUrl(
  reference: JournalEditionDTO[],
  edition: JournalEditionShortDTO,
): string {
  const tab = reference.find((row) => row.id === edition.parent);

  return tab && !isFirstJournalTab(reference, tab)
    ? `${JOURNAL_BASE_PATH}/${tab.slug}/${edition.slug}`
    : `${JOURNAL_BASE_PATH}/${edition.slug}`;
}

/**
 * '/journal/cre100/i883' — the current issue URL, which has to be kept alive.
 *
 * Under the edition slug alone: '/journal/special/cre100/i883' is a 404 on
 * cre.ru. The id is the row's own: the import wrote the old numbers back and
 * reset the sequence, so 884 is "CRE 4(459)" here just as it is on the live site
 */
export function getJournalIssueUrl(journal: JournalListItemDTO): string {
  return `${JOURNAL_BASE_PATH}/${journal.edition.slug}/${JOURNAL_ISSUE_URL_PREFIX}${journal.id}`;
}

/** '/journal/cre100/y2025' — an archive year, under the slug like an issue */
export function getJournalYearUrl(
  edition: JournalEditionShortDTO,
  year: number,
): string {
  return `${JOURNAL_BASE_PATH}/${edition.slug}/${JOURNAL_YEAR_URL_PREFIX}${year}`;
}

/**
 * What a /journal address points at. Issues and years are told by the last
 * segment ('i881', 'y2025') and hang off the edition slug one segment up. The
 * rest is a slug of the reference, with at most one segment before it — the
 * way cre.ru answers: '/journal/foo/cre100' is CRE 100, '/journal/a/b/cre100'
 * is a 404
 */
export type JournalSectionRoute =
  | { kind: 'issue'; editionSlug: string; issueId: number }
  | { kind: 'year'; editionSlug: string; year: number }
  | { kind: 'advertising' }
  /** `prefixed` — a segment stood before the slug, as in '/journal/guides/cid' */
  | { kind: 'slug'; slug: string; prefixed: boolean }
  | { kind: 'unknown' };

const ISSUE_SEGMENT = new RegExp(`^${JOURNAL_ISSUE_URL_PREFIX}(\\d+)$`);
const YEAR_SEGMENT = new RegExp(`^${JOURNAL_YEAR_URL_PREFIX}(\\d{4})$`);

export function parseJournalSectionPath(path: string): JournalSectionRoute {
  const segments = normalizeJournalPath(path)
    .slice(JOURNAL_BASE_PATH.length)
    .split('/')
    .filter(Boolean);

  const last = segments.at(-1) ?? '';
  const editionSlug = segments.at(-2);

  if (editionSlug) {
    const issue = ISSUE_SEGMENT.exec(last);

    if (issue) {
      return { kind: 'issue', editionSlug, issueId: Number(issue[1]) };
    }

    const year = YEAR_SEGMENT.exec(last);

    if (year) {
      return { kind: 'year', editionSlug, year: Number(year[1]) };
    }
  }

  if (segments.length === 1 && last === JOURNAL_ADVERTISING_SLUG) {
    return { kind: 'advertising' };
  }

  if (segments.length === 0 || segments.length > 2) {
    return { kind: 'unknown' };
  }

  return {
    kind: 'slug',
    slug: last.toLowerCase(),
    prefixed: segments.length === 2,
  };
}

export function findJournalRowBySlug(
  reference: JournalEditionDTO[],
  slug: string,
): JournalEditionDTO | null {
  const normalized = slug.toLowerCase();

  return reference.find((row) => row.slug === normalized) ?? null;
}

/**
 * The page of a /journal address: a tab, or an edition together with the tab
 * whose chips it belongs to. Null means the address names nothing — a 404
 */
export type JournalPageTarget =
  | { kind: 'tab'; tab: JournalEditionDTO; edition: null }
  | { kind: 'edition'; tab: JournalEditionDTO; edition: JournalEditionDTO }
  | { kind: 'advertising' };

export function resolveJournalPage(
  reference: JournalEditionDTO[],
  path: string,
): JournalPageTarget | null {
  const route = parseJournalSectionPath(path);

  if (route.kind === 'advertising') return { kind: 'advertising' };

  if (normalizeJournalPath(path) === JOURNAL_BASE_PATH) {
    const first = getJournalTabs(reference)[0];

    return first ? { kind: 'tab', tab: first, edition: null } : null;
  }

  if (route.kind !== 'slug') return null;

  const row = findJournalRowBySlug(reference, route.slug);

  if (!row) return null;

  if (row.parent === null) {
    // A tab is addressed by its slug alone, never under another segment
    return route.prefixed ? null : { kind: 'tab', tab: row, edition: null };
  }

  const tab = reference.find((item) => item.id === row.parent);

  return tab ? { kind: 'edition', tab, edition: row } : null;
}

export function isJournalListingRoute(route: {
  name?: unknown;
  path: string;
}): boolean {
  if (route.name === JOURNAL_ROUTE_NAMES.index) return true;
  if (route.name !== JOURNAL_ROUTE_NAMES.section) return false;

  const { kind } = parseJournalSectionPath(route.path);

  return kind !== 'issue' && kind !== 'year';
}
